// src/pages/SignUp.jsx
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import cross from "../../assets/icons/cross.svg";
import img from "../../assets/login/login1.png";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/logowhite.png";
import { useTheme } from "../../context/ThemeProvider";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google"; // Correct usage
import { FcGoogle } from "react-icons/fc";
import { HiOutlineMail } from "react-icons/hi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { toast } from "react-toastify";
import { normalizeEmail } from "../../utils/normalizeEmail";
import ActiveLoginSessionsGate, {
  isMaxSessionsError,
} from "../SignIn/ActiveLoginSessionsGate";
import { stripSpaces, blockSpaceKey, handlePasteStripSpaces } from "../../utils/stripSpaces";
import { lockBodyScroll } from "../../utils/lockBodyScroll";
import AuthVerifyingOverlay from "../AuthVerifyingOverlay";

/** 95% of default modal text / input metrics; spacing utilities unchanged */
const sm95 = "text-[0.83125rem]";
const xs95 = "text-[0.7125rem]";
const xl95 = "text-[1.1875rem]";
const h295 = "text-[1.425rem]";
const inp95 =
  "text-[0.83125rem] py-[0.475rem] px-[0.95rem] leading-normal";
const inpPw95 =
  "text-[0.83125rem] py-[0.475rem] pl-[0.95rem] pr-[2.375rem] leading-normal";
const otpBox95 = "w-[2.85rem] h-[2.85rem]";

/** UI cooldown before "Resend code" — server OTP expiry stays 10m (see backend authController). */
const SIGNUP_RESEND_COOLDOWN_SEC = 60;

const SignUpContent = ({ setShowModal, switchToSignIn, redirectAfterLogin }) => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { darkMode } = useTheme();
  const location = useLocation();

  // All state declarations first
  const [showEmailInputs, setShowEmailInputs] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [isGoogleVerifying, setIsGoogleVerifying] = useState(false);
  const [deviceLimitState, setDeviceLimitState] = useState(null);
  const [successMessage] = useState("");
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
  const passwordSpecialCharLookahead =
    /(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/;

  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [confirmInlineError, setConfirmInlineError] = useState("");
  const passwordInputRef = useRef(null);
  const passwordFieldWrapRef = useRef(null);

  // OTP verification states
  const [otpStep, setOtpStep] = useState(1); // 1: form, 2: OTP verification
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);

  // Effects
  useLayoutEffect(() => {
    return lockBodyScroll();
  }, []);

  // Resend cooldown only (not related to server OTP expiry window)
  useEffect(() => {
    if (resendCooldownSec > 0) {
      const timer = setTimeout(
        () => setResendCooldownSec(resendCooldownSec - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldownSec]);

  // Google Login Hook - correctly passing "credential"
  const finishGoogleSignup = (accessToken, message) => {
    const decodedToken = jwtDecode(accessToken);
    setAuth({
      ...decodedToken,
      RawToken: accessToken,
    });

    toast.success(message || "Google signup successful! Welcome to Shotkut.");

    if (redirectAfterLogin) {
      navigate(redirectAfterLogin);
    } else {
      // Default: stay on current page or go to dashboard
      const currentPath = location.pathname;
      if (currentPath === '/' || currentPath.includes('search') || currentPath.includes('category') || currentPath.includes('collection') || currentPath.includes('video')) {
        navigate(currentPath); // Stay on current page
      } else {
        navigate('/dashboard');
      }
    }

    setShowModal(false);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      console.log(
        "Google login SUCCESS credentialResponse:",
        credentialResponse
      ); // ✅ Log 1
      try {
        setIsGoogleVerifying(true);
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/google`,
          {
            credential: credentialResponse.access_token,
          }
        );
        console.log("Backend /auth/google RESPONSE:", response.data); // ✅ Log 2

        const { accessToken, message } = response.data;
        finishGoogleSignup(accessToken, message);
      } catch (error) {
        console.error("Error sending credential to backend:", error); // ✅ Log 4
        if (isMaxSessionsError(error)) {
          setDeviceLimitState({
            sessions: error.response.data.activeLoginSessions || [],
            sessionManagementToken:
              error.response.data.sessionManagementToken || "",
          });
          toast.info(
            error.response.data.message ||
              "You're already logged in on 2 devices. Please log out of one to continue.",
          );
          return;
        }
        toast.error("Google signup failed. Please try again.");
      } finally {
        setIsGoogleVerifying(false);
      }
    },
    onError: (error) => {
      console.error("Google login FAILED:", error); // ✅ Log 5
      toast.error("Google signup failed. Please try again.");
    },
    flow: "implicit",
  });

  const handleEmailChange = (e) => {
    const value = stripSpaces(e.target.value);
    setFormData({ ...formData, email: value });
  };

  const handleChange = (e) => {
    const value = stripSpaces(e.target.value);
    setFormData({ ...formData, [e.target.name]: value });
    if (e.target.name === "password" || e.target.name === "confirmPassword") {
      setConfirmInlineError("");
    }
  };

  const handlePasswordWrapBlur = (e) => {
    const next = e.relatedTarget;
    if (
      next &&
      passwordFieldWrapRef.current &&
      passwordFieldWrapRef.current.contains(next)
    ) {
      return;
    }
    window.setTimeout(() => {
      const el = passwordFieldWrapRef.current;
      if (el && !el.contains(document.activeElement)) {
        setShowPasswordRules(false);
      }
    }, 0);
  };

  const pwd = formData.password;
  const passwordRulesMet = {
    minLen: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    special: passwordSpecialCharLookahead.test(pwd),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (showEmailInputs) {
      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();
      const email = formData.email.trim();
      const password = formData.password.trim();
      const confirmPassword = formData.confirmPassword.trim();

      if (!firstName)
        newErrors.firstName = "First Name is required";
      if (!lastName)
        newErrors.lastName = "Last Name is required";
      if (!email) newErrors.email = "Email is required";
      else if (!emailRegex.test(email))
        newErrors.email = "Enter a valid email";
      if (!password) newErrors.password = true;
      else if (!passwordRegex.test(formData.password)) newErrors.password = true;

      if (!confirmPassword)
        newErrors.confirmPassword = "Please confirm your password";
      else if (password && confirmPassword && password !== confirmPassword)
        newErrors.confirmPassword = "Passwords do not match";
    }

    if (!agreedToPolicy) {
      toast.error("Please agree to the Privacy Policy and Terms & Conditions.");
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      if (newErrors.confirmPassword) {
        setConfirmInlineError(newErrors.confirmPassword);
      } else {
        setConfirmInlineError("");
      }

      const toastMessages = [
        newErrors.firstName,
        newErrors.lastName,
        newErrors.email,
      ].filter(Boolean);
      if (toastMessages.length > 0) {
        toast.error(toastMessages.join(". "));
      }

      if (newErrors.password) {
        setShowPasswordRules(true);
        passwordInputRef.current?.focus();
      }

      return;
    }

    setConfirmInlineError("");

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/send-signup-otp`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: normalizeEmail(formData.email),
          password: formData.password,
        }
      );

      if (response.data.success) {
        toast.success("Verification code sent to your email!");
        setOtpStep(2);
        setResendCooldownSec(SIGNUP_RESEND_COOLDOWN_SEC);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send verification code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`signup-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`signup-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the complete 6-digit verification code.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/verify-signup-otp`,
        { email: normalizeEmail(formData.email), otp: otpString },
        { withCredentials: true }
      );

      if (response.data.success) {
        const { accessToken } = response.data;
        const decodedToken = jwtDecode(accessToken);

        setAuth({
          ...decodedToken,
          RawToken: accessToken,
        });
        toast.success("Account created successfully! Welcome to Shotkut.");
        
        // Handle redirect based on context
        if (redirectAfterLogin) {
          navigate(redirectAfterLogin);
        } else {
          // Default: stay on current page or go to dashboard
          const currentPath = location.pathname;
          if (currentPath === '/' || currentPath.includes('search') || currentPath.includes('category') || currentPath.includes('collection') || currentPath.includes('video')) {
            navigate(currentPath); // Stay on current page
          } else {
            navigate('/dashboard');
          }
        }
        
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const errorMessage = error.response?.data?.message || "Invalid verification code. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/resend-signup-otp`,
        { email: normalizeEmail(formData.email) }
      );

      if (response.data.success) {
        toast.success("New verification code sent to your email!");
        setOtp(["", "", "", "", "", ""]);
        setResendCooldownSec(SIGNUP_RESEND_COOLDOWN_SEC);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to resend verification code. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setOtpStep(1);
    setOtp(["", "", "", "", "", ""]);
    setResendCooldownSec(0);
  };

  const formatResendCooldownLabel = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div
     id="signup-modal"
      className="bg-[#121212CC] h-screen fixed inset-0 z-50 modalOverflow"
      onClick={() => {
        if (!isGoogleVerifying) setShowModal(false);
      }}
    >
      {isGoogleVerifying && (
        <AuthVerifyingOverlay
          message="Logging you in"
          darkMode={darkMode}
        />
      )}
      <div className="bigscreen max-h-[97vh] h-[97vh] md:p-[2.425rem]">
        <div
          className={`overflow-y-auto relative max-w-[1024px] mx-auto h-full max-h-full border p-[1.212rem] pt-[3.395rem] md:p-[1.94rem] ${darkMode
              ? "bg-[#10130D] border-[#1E1E1E]"
              : "bg-white border-[#CBCBCB]"
            } shadow md:grid grid-cols-1 md:grid-cols-2 !gap-[1.94rem]`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`h-8 absolute top-3 md:top-4 right-4 ${
              isGoogleVerifying ? "pointer-events-none opacity-40" : "cursor-pointer"
            }`}
            onClick={() => {
              if (!isGoogleVerifying) setShowModal(false);
            }}
          >
            <img src={cross} alt="close" className="h-full object-contain" />
          </div>

          <div className="h-full max-h-[150px] md:max-h-full w-full rounded-2xl">
            <img
              src={img}
              className="object-cover h-full max-h-full md:max-h-[calc(97vh-151px)] w-full rounded-2xl opacity-90"
              alt="login visual"
            />
          </div>

          <div className="flex flex-col gap-[0.97rem] py-[0.97rem] md:py-[2.91rem]">
            <div className="h-[1.455rem] md:h-[1.94rem]">
              <img
                src={darkMode ? logowhite : logo}
                className="object-contain h-full"
                alt="Logo"
              />
            </div>

            {deviceLimitState ? (
              <ActiveLoginSessionsGate
                darkMode={darkMode}
                sessions={deviceLimitState.sessions}
                sessionManagementToken={deviceLimitState.sessionManagementToken}
                onCancel={() => setDeviceLimitState(null)}
                onSignedIn={(accessToken) => {
                  setDeviceLimitState(null);
                  finishGoogleSignup(
                    accessToken,
                    "Other device logged out. You're signed in.",
                  );
                }}
              />
            ) : otpStep === 1 ? (
              <>
                <div className={`mt-2 md:mt-[1.94rem] ${h295} font-semibold`}>
                  Create a free account
                </div>

                <form onSubmit={handleSubmit}>
              <div className="flex flex-col lg:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => googleLogin()}
                  disabled={isGoogleVerifying || loading}
                  className={
                    darkMode
                      ? `input dark w-full lg:w-1/2 flex items-center justify-center gap-2 ${inp95}`
                      : `input w-full lg:w-1/2 flex items-center justify-center gap-2 ${inp95}`
                  }
                >
                  <FcGoogle className={xl95} /> Continue with Google
                </button>
                <button
                  type="button"
                  className={
                    darkMode
                      ? `input dark w-full lg:w-1/2 flex items-center justify-center gap-2 ${inp95}`
                      : `input w-full lg:w-1/2 flex items-center justify-center gap-2 ${inp95}`
                  }
                  onClick={() => setShowEmailInputs(true)}
                >
                  <HiOutlineMail className={xl95} /> Continue with Email
                </button>
              </div>

              {showEmailInputs && (
                <>
                  <div className="flex flex-row gap-4 mt-4 w-full">
                    <div className="relative flex-1 min-w-0">
                      <label
                        htmlFor="signup-first-name"
                        className={`block ${sm95} mb-1 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        First name{" "}
                        <span className="text-red-500" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="signup-first-name"
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First name"
                        autoComplete="given-name"
                        required
                        className={
                          darkMode
                            ? `input dark w-full ${inp95}`
                            : `input w-full ${inp95}`
                        }
                      />
                    </div>

                    <div className="relative flex-1 min-w-0">
                      <label
                        htmlFor="signup-last-name"
                        className={`block ${sm95} mb-1 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Last name{" "}
                        <span className="text-red-500" aria-hidden="true">
                          *
                        </span>
                      </label>
                      <input
                        id="signup-last-name"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last name"
                        autoComplete="family-name"
                        required
                        className={
                          darkMode
                            ? `input dark w-full ${inp95}`
                            : `input w-full ${inp95}`
                        }
                      />
                    </div>
                  </div>

                  <div className="relative mt-4">
                    <label
                      htmlFor="signup-email"
                      className={`block ${sm95} mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Email{" "}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <input
                      id="signup-email"
                      type="text"
                      inputMode="email"
                      name="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      onKeyDown={blockSpaceKey}
                      onPaste={(e) =>
                        handlePasteStripSpaces(e, (value) => {
                          setFormData((prev) => ({ ...prev, email: value }));
                        })
                      }
                      onBlur={() =>
                        setFormData((prev) => ({
                          ...prev,
                          email: stripSpaces(prev.email),
                        }))
                      }
                      placeholder="you@example.com"
                      autoComplete="email"
                      required
                      className={
                        darkMode
                          ? `input dark w-full ${inp95}`
                          : `input w-full ${inp95}`
                      }
                    />
                  </div>

                  <div
                    ref={passwordFieldWrapRef}
                    className="relative mt-4"
                    onBlur={handlePasswordWrapBlur}
                  >
                    <label
                      htmlFor="signup-password"
                      className={`block ${sm95} mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Password{" "}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        id="signup-password"
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onFocus={() => setShowPasswordRules(true)}
                        placeholder="Create a password"
                        autoComplete="new-password"
                        required
                        aria-describedby={
                          showPasswordRules &&
                          (!passwordRulesMet.minLen ||
                            !passwordRulesMet.upper ||
                            !passwordRulesMet.special)
                            ? "signup-password-rules"
                            : undefined
                        }
                        className={
                          darkMode
                            ? `input dark w-full ${inpPw95}`
                            : `input w-full ${inpPw95}`
                        }
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-0 bottom-0 my-auto h-fit text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showPassword ? (
                          <AiOutlineEyeInvisible className={xl95} />
                        ) : (
                          <AiOutlineEye className={xl95} />
                        )}
                      </button>
                    </div>
                    {showPasswordRules &&
                      (!passwordRulesMet.minLen ||
                        !passwordRulesMet.upper ||
                        !passwordRulesMet.special) && (
                        <ul
                          id="signup-password-rules"
                          className={`mt-2 ${xs95} space-y-1.5 rounded-md border px-3 py-2 list-none ${
                            darkMode
                              ? "border-[#333] bg-[#1a1a1a] text-gray-300"
                              : "border-gray-200 bg-gray-50 text-gray-700"
                          }`}
                        >
                          {!passwordRulesMet.minLen && (
                            <li>Min 8 characters</li>
                          )}
                          {!passwordRulesMet.upper && (
                            <li>1 uppercase letter</li>
                          )}
                          {!passwordRulesMet.special && (
                            <li>1 special character (!@#$… etc.)</li>
                          )}
                        </ul>
                      )}
                  </div>

                  <div className="relative mt-4">
                    <label
                      htmlFor="signup-confirm-password"
                      className={`block ${sm95} mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Confirm password{" "}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        id="signup-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        required
                        className={
                          darkMode
                            ? `input dark w-full ${inpPw95}`
                            : `input w-full ${inpPw95}`
                        }
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-0 bottom-0 my-auto h-fit text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showConfirmPassword ? (
                          <AiOutlineEyeInvisible className={xl95} />
                        ) : (
                          <AiOutlineEye className={xl95} />
                        )}
                      </button>
                    </div>
                    {confirmInlineError && (
                      <p className={`text-red-500 ${xs95} mt-1.5 font-medium`}>
                        {confirmInlineError}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="agree"
                      className="form-checkbox text-[var(--primary)] focus:ring-[var(--primary)]"
                      checked={agreedToPolicy}
                      onChange={(e) => setAgreedToPolicy(e.target.checked)}
                    />
                    <label htmlFor="agree" className={sm95}>
                      By creating an account, you confirm you are 18 or over, and
                      that you agree to our{" "}
                      <a
                        href="/privacypolicy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--primary)] underline hover:opacity-80"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="/termsandconditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--primary)] underline hover:opacity-80"
                      >
                        Terms and Conditions
                      </a>
                      .
                    </label>
                  </div>

                  <button
                    type="submit"
                    className={`greenButton ${sm95} mt-4 mx-auto ${
                      !agreedToPolicy || loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={!agreedToPolicy || loading}
                  >
                    {loading ? "Loading..." : "Create account"}
                  </button>

                  {successMessage && (
                    <p className={`text-green-500 ${sm95} text-center mt-2`}>
                      {successMessage}
                    </p>
                  )}
                </>
              )}
            </form>
            
            {/* Switch to Sign In */}
            <div className="text-center mt-4">
              <p className={`${sm95} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={switchToSignIn}
                  className="text-[var(--primary)] hover:underline font-medium"
                >
                  Sign In
                </button>
              </p>
            </div>
              </>
            ) : (
              <>
                {/* OTP Verification Step */}
                <div className="mt-2 md:mt-[1.94rem]">
                  <h2 className={`${h295} font-semibold`}>Verify Your Email</h2>
                  <p className={`${sm95} mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    We've sent a 6-digit verification code to <strong>{formData.email}</strong>
                  </p>
                </div>

                <div className="flex flex-col gap-[0.97rem] mt-[0.97rem]">
                  <div className="flex gap-2 justify-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`signup-otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className={`${otpBox95} text-center ${xl95} font-semibold ${
                          darkMode 
                            ? "bg-[#1E1E1E] border-[#333] text-white" 
                            : "bg-white border-gray-300 text-black"
                        } border-2 rounded-lg focus:border-[var(--primary)] focus:outline-none`}
                      />
                    ))}
                  </div>

                  <button 
                    onClick={handleVerifyOTP} 
                    className={`greenButton ${sm95} mt-2 mx-auto`}
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify & Create Account"}
                  </button>

                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldownSec > 0 || loading}
                    className={`${sm95} mx-auto hover:underline ${
                      resendCooldownSec > 0 || loading
                        ? "text-gray-500 cursor-not-allowed"
                        : darkMode 
                          ? "text-gray-400 hover:text-gray-300" 
                          : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    {resendCooldownSec > 0
                      ? `Resend code in ${formatResendCooldownLabel(resendCooldownSec)}`
                      : "Resend code"}
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToForm}
                    className={`${sm95} mx-auto hover:underline ${
                      darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    ← Change Email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SignUp = (props) => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
    <SignUpContent {...props} />
  </GoogleOAuthProvider>
);

export default SignUp;
